import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import { addManufacturer, updateManufacturer } from 'store/thunks/manufacturerManagementThunk';

// Matches the VARCHAR(255) column in core.
const MAX_LENGTH = 255;

// Errors about the name itself go under the field; anything else is shown as an
// alert inside the dialog so the user keeps what they typed.
const FIELD_ERROR_CODES = ['MANUFACTURER_ALREADY_EXISTS', 'INVALID_MANUFACTURER_NAME'];

// Mirrors core's normalisation (trim + collapse repeated spaces + upper case) so
// the user sees what will actually be saved.
const normalize = (value) => value.replace(/\s+/g, ' ').trim().toUpperCase();

/**
 * Add (no `manufacturer` prop) or rename (with `manufacturer`) dialog. A rename
 * only changes what is offered for new inventory items; existing items keep the
 * manufacturer they were saved with.
 */
const ManufacturerDialog = ({ open, manufacturer, onClose }) => {
  const dispatch = useDispatch();
  // The parent clears `manufacturer` on close; keep the last one so the title and
  // field don't flip to "Add" during the closing fade.
  const [target, setTarget] = useState(manufacturer);
  useEffect(() => {
    if (open) setTarget(manufacturer);
  }, [open, manufacturer]);

  const isEdit = !!target;
  const currentName = target?.manufacturer ?? '';

  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(manufacturer?.manufacturer ?? '');
      setFieldError('');
      setError('');
    }
  }, [open, manufacturer]);

  const cleaned = normalize(name);
  const unchanged = isEdit && cleaned === currentName;
  const showCleanedHint = cleaned.length > 0 && cleaned !== name;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!cleaned || unchanged) return;

    setSubmitting(true);
    setFieldError('');
    setError('');

    const request = isEdit
      ? updateManufacturer({ manufacturerId: target.manufacturerId, manufacturer: cleaned })
      : addManufacturer({ manufacturer: cleaned });

    dispatch(request)
      .then(() => onClose())
      .catch((submitError) => {
        const code = submitError?.response?.errors?.[0]?.extensions?.code;
        const message = getGraphQLErrorMessage(submitError);
        if (FIELD_ERROR_CODES.includes(code)) {
          setFieldError(message);
        } else {
          setError(message);
        }
      })
      .finally(() => setSubmitting(false));
  };

  let helperText = '';
  if (fieldError) {
    helperText = (
      <Typography variant='caption' color='error'>
        {fieldError}
      </Typography>
    );
  } else if (showCleanedHint) {
    helperText = `Will be saved as "${cleaned}"`;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
      <DialogTitle>{isEdit ? 'Edit Manufacturer' : 'Add Manufacturer'}</DialogTitle>
      <form onSubmit={handleSubmit} noValidate>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            label='Manufacturer Name'
            required
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (fieldError) setFieldError('');
            }}
            inputProps={{ maxLength: MAX_LENGTH }}
            error={!!fieldError}
            helperText={helperText}
            sx={{ mt: 1 }}
          />
          {isEdit && (
            <Alert severity='info' sx={{ mt: 2 }}>
              The new name is used for inventory items added from now on. Existing inventory items
              keep the manufacturer name they were saved with.
            </Alert>
          )}
          {error && (
            <Alert severity='error' sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color='secondary' disabled={submitting}>
            Cancel
          </Button>
          <LoadingButton
            loading={submitting}
            type='submit'
            variant='contained'
            disabled={!cleaned || unchanged}
          >
            {isEdit ? 'Save' : 'Add'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

ManufacturerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  manufacturer: PropTypes.shape({
    manufacturerId: PropTypes.string.isRequired,
    manufacturer: PropTypes.string.isRequired,
  }),
  onClose: PropTypes.func.isRequired,
};

ManufacturerDialog.defaultProps = {
  manufacturer: null,
};

export default ManufacturerDialog;
